import { imageSizes, imageSrcSet, imageUrl } from "@components/componentUtils"
import { AstroCareersViewTransitions } from "@components/AstroViewTransitions"
import CareersCard from "@components/Careers/CareersCard"
import { useRouteShellContext } from "@components/routeShellContext"
import { BackButton } from "@components/SiteShell"
import { SiteShell } from "@components/SiteShell"
import { careerDetailPageQueryOptions } from "@data/publicPageDataQuery"
import { sanitizeRichHtml } from "@lib/sanitizeHtml"
import * as m from "@paraglide/messages"
import type { CareerItem } from "@site-types/content"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { isSupportedLocale } from "@utils/locale"
import { localizedRouteHead } from "@utils/routeHead"

export const Route = createFileRoute("/$locale/careers/$slug")({
  beforeLoad: ({ params }) => {
    if (!isSupportedLocale(params.locale)) throw redirect({ to: "/404" })
  },
  loader: async ({ context, params }) => {
    const locale = params.locale as "en" | "ja"
    const data = await context.queryClient.ensureQueryData(careerDetailPageQueryOptions(locale, params.slug))
    if (!data) throw redirect({ href: `/${locale}/careers` })
    return data
  },
  head: ({ params, loaderData }) => {
    const locale = params.locale as "en" | "ja"
    const position = loaderData?.position
    const head = localizedRouteHead({
      locale,
      page: "careers",
      path: `/${locale}/careers/${params.slug}`,
      title: position?.title ? `${position.title} | PLAYBRAIN Inc.` : undefined,
      description: position?.newsBlurb,
      image: position?.newsCoverImage
        ? (imageUrl(position.newsCoverImage, "careerCard") ?? undefined)
        : undefined,
      type: "article",
    })
    if (position?.newsCoverImage) {
      head.links.push({
        rel: "preload",
        as: "image",
        href: imageUrl(position.newsCoverImage, "careerCard") ?? "",
        imageSrcSet: imageSrcSet(position.newsCoverImage, "careerCard"),
        imageSizes: imageSizes("careerCard"),
        fetchPriority: "high",
      })
    }
    return head
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { position } = Route.useLoaderData()
  return (
    <SiteShell>
      <CareerDetailPage position={position} />
    </SiteShell>
  )
}

export function CareerDetailPage({ position }: { position: CareerItem }) {
  const { locale } = useRouteShellContext()
  return (
    <>
      <AstroCareersViewTransitions careers={[position]} />
      <div className="back-button">
        <BackButton />
      </div>
      <div className="career-detail-container">
        <div className="sidebar">
          <CareersCard position={position} />
        </div>
        <div className="intro">
          <h1 className="title">{position.title}</h1>
          <p className="blurb cut-corners">{position.newsBlurb}</p>
        </div>
        <article className="post">
          <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(position.description) }} />
          <a
            className="apply-link"
            href={`/${locale}/contact/${position.slug?.current}`}
          >
            <div className="apply">
              <div className="cut-corners">
                <h3>{m.careers_post_apply({}, { locale: locale }).toUpperCase()}</h3>
                <p>{m.careers_post_apply_description({}, { locale: locale })}</p>
              </div>
            </div>
          </a>
        </article>
      </div>
    </>
  )
}
